import { HttpClient, HttpParams } from '@angular/common/http';
import { effect, inject, Injectable, NgZone } from '@angular/core';
import { AuthService } from '@nomoh-monorepo/data-access-auth';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import {
  HandleEvent,
  ICreateWhatsappInstance,
  InstanceBehaviorModel,
  ISession,
  IWhatsappConfig,
  IWhatsappConnectionStatus,
  IWhatsappInstanceData,
  IWhatsappLogEntry,
  IWhatsappSessionAction,
  IWhatsappSessionResponse,
} from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';
import { EstabelecimentoDataService } from '../feature-estabelecimento/estabelecimento.dataservice';
import { WhatsappConfigDataService } from './whatsapp-config.dataservice';
@Injectable({
  providedIn: 'root',
})
export class WhatsappConfigService {
  //#region injections
  private http = inject(HttpClient);
  private envService = inject(EnvironmentService);
  private ngZone = inject(NgZone);

  private whatsappConfigDataService = inject(WhatsappConfigDataService);

  private estabelecimentoDataService = inject(EstabelecimentoDataService);

  private authService = inject(AuthService);
  //#endregion

  private get apiBase(): string {
    return this.envService.getApiUrl('bardo');
  }

  private get whatsappUrl(): string {
    return `${this.apiBase}/whatsapp`;
  }

  constructor() {
    // Efeito para logar mudanças na configuração do WhatsApp
    effect(() => {
      const whatsAppAuth = this.whatsappConfigDataService.$whatsappAuth();
      const sessionName = this.whatsappConfigDataService.$sessionName();

      if (whatsAppAuth) {
        const payload = this.buildCreateInstancePayload(sessionName);
        this.registrarInstancia(payload);
      }
    });
  }

  //#region chamadas à API para controle da sessão do WhatsApp
  async criarInstancia(session: ISession): Promise<IWhatsappSessionResponse> {
    this.whatsappConfigDataService.setLoading(true);
    const payload = this.normalizarSessao(session);

    const resposta = await firstValueFrom(
      this.http.post<IWhatsappSessionResponse>(
        `${this.whatsappUrl}/instance`,
        payload
      )
    );

    this.adicionarLog('info', `Sessão enviada para inicialização.`);
    return resposta;
  }

  async registrarInstancia(
    payload: ICreateWhatsappInstance
  ): Promise<IWhatsappSessionResponse> {
    const sessionName = payload.sessionName?.trim();
    const pessoaInclusao = payload.pessoaInclusao?.trim();
    const fluxoAtendimentoId = payload.fluxoAtendimentoId;

    if (!sessionName) {
      throw new Error('sessionName é obrigatório para registrar a instância.');
    }

    if (
      !fluxoAtendimentoId ||
      fluxoAtendimentoId.toString().trim().length === 0
    ) {
      throw new Error(
        'fluxoAtendimentoId inválido para registrar a instância.'
      );
    }

    if (!pessoaInclusao) {
      throw new Error(
        'Informe quem está registrando a instância (pessoaInclusao).'
      );
    }

    const body: ICreateWhatsappInstance = {
      sessionName,
      fluxoAtendimentoId,
      pessoaInclusao: pessoaInclusao.slice(0, 30),
    };

    const resposta = await firstValueFrom(
      this.http.post<IWhatsappSessionResponse>(
        `${this.whatsappUrl}/instance/create`,
        body
      )
    );

    this.adicionarLog(
      'success',
      resposta.message ?? 'Instância registrada com sucesso.'
    );
    return resposta;
  }

  private buildCreateInstancePayload(
    sessionName: string
  ): ICreateWhatsappInstance {
    const estabelecimento =
      this.estabelecimentoDataService.$estabelecimentoAtual();
    const fluxoAtendimentoId = estabelecimento?.fluxosAtendimento?.[0]?.id;

    return {
      sessionName,
      fluxoAtendimentoId: String(fluxoAtendimentoId),
      pessoaInclusao: this.resolvePessoaInclusao(),
    };
  }

  private resolvePessoaInclusao(): string {
    const usuario = this.authService.$user();
    const candidato =
      usuario?.email?.trim() ||
      usuario?.name?.trim() ||
      usuario?.id?.toString() ||
      'system';

    return candidato.length > 30 ? candidato.slice(0, 30) : candidato;
  }
  async reiniciarSessao(sessionName: string): Promise<{ message: string }> {
    const resposta = await firstValueFrom(
      this.http.post<{ message: string }>(
        `${this.whatsappUrl}/restartSession`,
        this.buildSessionAction(sessionName)
      )
    );
    this.adicionarLog('info', resposta?.message ?? 'Sessão reiniciada.');
    return resposta;
  }

  async encerrarSessao(sessionName: string): Promise<{ message: string }> {
    const resposta = await firstValueFrom(
      this.http.post<{ message: string }>(
        `${this.whatsappUrl}/closeSession`,
        this.buildSessionAction(sessionName)
      )
    );
    this.adicionarLog('warning', resposta?.message ?? 'Sessão encerrada.');
    return resposta;
  }

  async excluirSessao(sessionName: string): Promise<{ message: string }> {
    const resposta = await firstValueFrom(
      this.http.request<{ message: string }>(
        'DELETE',
        `${this.whatsappUrl}/deleteSession`,
        {
          body: this.buildSessionAction(sessionName),
        }
      )
    );
    this.adicionarLog('warning', resposta?.message ?? 'Instância removida.');

    this.ngZone.run(() => {
      const config = {
        ...this.whatsappConfigDataService.$whatsappConfig(),
        habilitado: false,
        telefone: undefined,
        ultimaConexao: undefined,
        status: 'desconectado',
      } as IWhatsappConfig;
      this.whatsappConfigDataService.updateWhatsappConfig(config);
      this.whatsappConfigDataService.setWhatsappConectado(false);
    });
    return resposta;
  }

  async atualizarConfiguracoesComportamento(payload: ISession): Promise<void> {
    this.whatsappConfigDataService.setError(null);

    try {
      await firstValueFrom(
        this.http.post<{ message: string }>(
          `${this.whatsappUrl}/updateSettings`,
          payload,
          {
            withCredentials: true,
          }
        )
      );

      // Atualiza config local após sucesso
      const configAtual = this.whatsappConfigDataService.$whatsappConfig();

      if (configAtual) {
        this.ngZone.run(() => {
          this.whatsappConfigDataService.setWhatsappConfig({
            ...configAtual,
            settings: payload.settings,
          });
        });
      }

      this.adicionarLog(
        'success',
        'Configurações de comportamento atualizadas em tempo real.'
      );
    } catch (error) {
      this.whatsappConfigDataService.setError(
        'Erro ao atualizar configurações de comportamento.'
      );
      this.adicionarLog(
        'error',
        'Falha ao atualizar configurações de comportamento.'
      );
      throw error;
    } finally {
      this.whatsappConfigDataService.setLoading(false);
    }
  }

  async testarConexao(sessionName: string): Promise<IWhatsappConnectionStatus> {
    const trimmed = sessionName?.trim();
    if (!trimmed) {
      throw new Error('Informe o nome da sessão para testar.');
    }

    const resposta = await firstValueFrom(
      this.http.get<IWhatsappConnectionStatus>(`${this.whatsappUrl}/status`, {
        params: new HttpParams().set('sessionName', trimmed),
      })
    );

    this.adicionarLog(
      resposta.connected ? 'success' : 'warning',
      resposta.message
    );

    this.ngZone.run(() => {
      this.whatsappConfigDataService.setWhatsappConfig({
        ...this.whatsappConfigDataService.$whatsappConfig(),
        habilitado: resposta.connected,
        status: resposta.connected ? 'conectado' : 'desconectado',
      } as IWhatsappConfig);
      this.whatsappConfigDataService.setWhatsappConectado(resposta.connected);
    });

    return resposta;
  }

  async buscarDadosInstancia(): Promise<void> {
    const trimmed = this.whatsappConfigDataService.$sessionName().trim();
    if (!trimmed) {
      throw new Error('Informe o nome da sessão para buscar dados.');
    }

    const resposta = await firstValueFrom(
      this.http.post<IWhatsappInstanceData>(
        `${this.whatsappUrl}/getInstance`,
        this.buildSessionAction(trimmed)
      )
    );

    this.ngZone.run(() => {
      const configAtual = this.whatsappConfigDataService.$whatsappConfig();
      if (configAtual) {
        this.whatsappConfigDataService.setWhatsappConfig({
          ...configAtual,
          telefone: resposta.telefone ?? configAtual.telefone,
          settings: resposta.settings ?? configAtual.settings,
          webhookUrl: resposta.webhook ?? configAtual.webhookUrl,
          ultimaConexao: resposta.dataAtualizacao ?? configAtual.ultimaConexao,
        });
      }
    });
  }
  //#endregion

  private normalizarSessao(session: ISession): ISession {
    const sessionName = session.sessionName?.trim();
    const webhookUrl = session.webhookUrl?.trim();

    if (!sessionName || !webhookUrl) {
      throw new Error('sessionName e webhookUrl são obrigatórios.');
    }

    const normalized: ISession = {
      sessionName,
      webhookUrl,
      settings: {
        ...this.defaultSettings(),
        ...(session.settings ?? {}),
      },
    };

    // Preserva phoneNumber se fornecido (para pairing code)
    if ('phoneNumber' in session && session.phoneNumber) {
      (normalized as any).phoneNumber = session.phoneNumber;
    }

    return normalized;
  }

  defaultSettings(): InstanceBehaviorModel {
    return {
      rejectCalls: true,
      alwaysOnline: true,
      ignoreGroups: true,
      ignoreBroadcasts: true,
      automaticSchedule: true,
      automaticScheduleConfirmation: true,
      webhookEvents: [
        HandleEvent.HandleAck,
        HandleEvent.HandleStateChange,
        HandleEvent.HandleMessage,
      ],
    };
  }

  private buildSessionAction(sessionName: string): IWhatsappSessionAction {
    const value = sessionName?.trim();
    if (!value) {
      throw new Error('Informe o nome da sessão.');
    }
    return { sessionName: value };
  }

  private adicionarLog(
    tipo: 'info' | 'success' | 'warning' | 'error',
    mensagem: string
  ): void {
    const novo: IWhatsappLogEntry = {
      id: Date.now(),
      tipo,
      mensagem,
      timestamp: new Date(),
    };

    this.whatsappConfigDataService.addLogs(novo);
  }

  async deleteWhatsappAuth(fluxoAtendimentoId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.whatsappUrl}/deleteWhatsappAuth`, {
        params: new HttpParams().set('fluxoAtendimentoId', fluxoAtendimentoId),
      })
    );
  }

  async iniciarConexao(
    phoneNumber: string | undefined = undefined
  ): Promise<void> {
    this.whatsappConfigDataService.garantirSocketConectado();

    const sessionPayload = this.buildSessionPayload(phoneNumber);
    const config = {
      ...this.whatsappConfigDataService.$whatsappConfig(),
      status: 'conectando',
      telefone: undefined,
      ultimaConexao: undefined,
      fluxoAtendimentoId:
        this.estabelecimentoDataService.$estabelecimentoAtual()
          ?.fluxosAtendimento?.[0]?.id,
      webhookUrl:
        this.estabelecimentoDataService.$estabelecimentoAtual()
          ?.fluxosAtendimento?.[0]?.tipoFluxo?.webHookUrl,
      qrCode: this.whatsappConfigDataService.$qrCodeB64(),
    } as IWhatsappConfig;

    this.whatsappConfigDataService.setWhatsappConfig(config);

    await this.criarInstancia(sessionPayload);
  }

  private buildSessionPayload(
    phoneNumber: string | undefined = undefined
  ): ISession {
    const webhookUrl =
      this.estabelecimentoDataService.$estabelecimentoAtual()
        ?.fluxosAtendimento?.[0]?.tipoFluxo?.webHookUrl;

    if (!this.whatsappConfigDataService.$sessionName() || !webhookUrl) {
      throw new Error('Nome da sessão ou webhook não disponíveis.');
    }

    return {
      sessionName: this.whatsappConfigDataService.$sessionName(),
      webhookUrl,
      settings: this.defaultSettings(),
      phoneNumber,
    };
  }
}
